import { forwardRef, useEffect, useRef } from "react";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

export const FiltersButton = forwardRef<
  HTMLButtonElement,
  {
    open: boolean;
    activeCount: number;
    onClick: () => void;
  }
>(({ open, activeCount, onClick }, ref) => (
  <Button
    ref={ref}
    type="button"
    variant="outline"
    aria-expanded={open}
    aria-controls="list-filters-panel"
    onClick={onClick}
    data-testid="button-list-filters"
  >
    <Filter className="mr-2 h-4 w-4" />
    Filters{activeCount > 0 ? ` (${activeCount})` : ""}
  </Button>
));
FiltersButton.displayName = "FiltersButton";

export function FiltersPanel({
  open,
  onCloseFocus,
  children,
}: {
  open: boolean;
  onCloseFocus: () => void;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      requestAnimationFrame(() => {
        panelRef.current
          ?.querySelector<HTMLElement>("button:not([disabled]), input:not([disabled]), [tabindex='0']")
          ?.focus();
      });
    } else if (!open && wasOpen.current) {
      onCloseFocus();
    }
    wasOpen.current = open;
  }, [open, onCloseFocus]);

  if (!open) return null;
  return (
    <div
      ref={panelRef}
      id="list-filters-panel"
      role="region"
      aria-label="List filters"
      className="rounded-xl border border-border bg-card p-5 shadow-sm"
    >
      {children}
    </div>
  );
}

export function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}
